import React, { useState } from 'react';
import Table, { CellProps, SortType } from 'rsuite/Table';
import { DeliveryMethodService } from '../../../services/deliveryMethod.service';
import { AlertService } from '../../../services/alert.service';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import useSWR from 'swr';
import { api, fetcher } from '../../../services/api';
import { ToggleCell } from '../../common/table/toggle-cell';
import { useNavigate } from 'react-router-dom';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import {
  useDeliveryMethods,
  useOrderCategories,
} from '../../../consts/db-values';
import { SupplierCell } from '../../common/table/supplier-cell';
import {
  CleanDeliveryMethod,
  RbacObject,
} from '../../../export-types/cleaned-types';
import {
  SupplierSelect,
  useSelectSupplierQs,
} from '../purchase-order/supplier-select';
import { Rbac } from '../../common/Rbac';
import {
  compareBooleanValues,
  compareNumberValues,
  compareStrings,
} from './delivery-methods-tab.sortingHelpers';
import classNames from 'classnames';

export const DeliveryMethodsTab = () => {
  const DELIVERY_METHODS = useDeliveryMethods();
  const ORDER_CATEGORIES = useOrderCategories();

  const navigate = useNavigate();

  const [selectSupplier] = useSelectSupplierQs();

  const { data, mutate, isValidating } = useSWR<Array<CleanDeliveryMethod>>(
    '/delivery-method',
    fetcher,
  );

  const [sortColumn, setSortColumn] = React.useState<string | undefined>();
  const [sortType, setSortType] = React.useState<SortType | undefined>();
  const [loading, setLoading] = useState<boolean>();

  const sortedAndFilteredData = React.useMemo(() => {
    let updateData = data || [];

    if (selectSupplier) {
      updateData = updateData.filter(
        (item) => item.supplier && item.supplier.id === selectSupplier,
      );
    }

    if (sortColumn && sortType && updateData.length > 0) {
      return [...updateData].sort((a, b) => {
        switch (sortColumn) {
          case 'destination':
          case 'method':
          case 'name':
          case 'productCategories':
            return compareStrings(
              a,
              b,
              sortColumn as keyof CleanDeliveryMethod,
              sortType,
              ORDER_CATEGORIES,
            );
          case 'disabled':
          case 'forCalculator':
            return compareBooleanValues(
              a,
              b,
              sortColumn as keyof CleanDeliveryMethod,
              sortType,
            );
          case 'deliveryPeriodFrom':
          case 'minWeight':
          case 'maxWeight':
            return compareNumberValues(
              a,
              b,
              sortColumn as keyof CleanDeliveryMethod,
              sortType,
            );
          default:
            return 0;
        }
      });
    }

    updateData.sort((a, b) => {
      return (
        a.deliveryPeriodFrom - b.deliveryPeriodFrom ||
        a.productCategories[0].localeCompare(b.productCategories[0])
      );
    });

    return updateData;
  }, [data, selectSupplier, sortColumn, sortType]);

  const handleSortColumn = (
    dataKey: string,
    sortType?: SortType | undefined,
  ) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSortColumn(dataKey);
      setSortType(sortType);
    }, 500);
  };

  async function checkDeliveryMethod(
    rowData: CleanDeliveryMethod,
    value: boolean,
  ) {
    await DeliveryMethodService.updateDeliveryMethod(
      { disabled: value },
      rowData.id,
    );

    await mutate();

    AlertService.success();
  }

  async function onChangeForCalculator(
    rowData: CleanDeliveryMethod,
    value: boolean,
  ) {
    if (!value) return;

    await api.put(`/delivery-method/${rowData.id}/for-calculator`);

    await mutate();

    AlertService.success();
  }

  const DeliveryMethodCell = ({
    rowData,
    ...props
  }: CellProps<CleanDeliveryMethod>) =>
    rowData && (
      <Table.Cell {...props}>
        {DELIVERY_METHODS[rowData.method as keyof typeof DELIVERY_METHODS]}
      </Table.Cell>
    );

  const PeriodCell = ({ rowData, ...props }: CellProps<CleanDeliveryMethod>) =>
    rowData && (
      <Table.Cell {...props}>
        {rowData.deliveryPeriodFrom}
        {rowData.deliveryPeriodTo ? ` — ${rowData.deliveryPeriodTo}` : ''} дней
      </Table.Cell>
    );

  const CategoryCell = ({
    rowData,
    ...props
  }: CellProps<CleanDeliveryMethod>) =>
    rowData && (
      <Table.Cell {...props}>
        {rowData.productCategories
          .map(
            (c: string) => ORDER_CATEGORIES[c as keyof typeof ORDER_CATEGORIES],
          )
          .join(', ')}
      </Table.Cell>
    );
  const MinWeight = ({ rowData, ...props }: CellProps<CleanDeliveryMethod>) =>
    rowData &&
    rowData.minWeight && (
      <Table.Cell {...props}>{rowData.minWeight}</Table.Cell>
    );
  const MaxWeight = ({ rowData, ...props }: CellProps<CleanDeliveryMethod>) =>
    rowData &&
    rowData.maxWeight && (
      <Table.Cell {...props}>{rowData.maxWeight}</Table.Cell>
    );
  const renderMenu =
    (deliveryMethod: CleanDeliveryMethod): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 1) {
          navigate(`/deliveries/delivery-method`, { state: deliveryMethod });
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={1}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={2}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanDeliveryMethod>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  const DeliveryMethodNameCell = ({
    rowData,
    ...props
  }: CellProps<CleanDeliveryMethod>) =>
    rowData && <Table.Cell {...props}>{rowData.name}</Table.Cell>;

  return (
    <>
      <div className="d-flex w-75 mr-auto z-3">
        <Rbac object={RbacObject.Supplier} action={'read:admin'}>
          <SupplierSelect />
        </Rbac>
      </div>

      <Table
        data={sortedAndFilteredData}
        sortColumn={sortColumn}
        sortType={sortType}
        loading={
          (isValidating && sortedAndFilteredData.length === 0) || loading
        }
        headerHeight={57}
        autoHeight
        onSortColumn={handleSortColumn}
      >
        <Table.Column width={150} fullText>
          <Table.HeaderCell>Поставщик</Table.HeaderCell>
          <SupplierCell />
        </Table.Column>
        <Table.Column width={150} fullText>
          <Table.HeaderCell>Откуда</Table.HeaderCell>
          <Table.Cell>
            {(rowData) => {
              return (rowData as CleanDeliveryMethod).supplier?.location?.name;
            }}
          </Table.Cell>
        </Table.Column>
        <Table.Column width={150} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Куда
          </Table.HeaderCell>

          <Table.Cell dataKey="destination">
            {(rowData) => {
              return (rowData as CleanDeliveryMethod).destination?.name;
            }}
          </Table.Cell>
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Способ доставки
          </Table.HeaderCell>
          <DeliveryMethodCell dataKey="method" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Название
          </Table.HeaderCell>
          <DeliveryMethodNameCell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Срок доставки
          </Table.HeaderCell>
          <PeriodCell dataKey="deliveryPeriodFrom" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Категории товаров
          </Table.HeaderCell>
          <CategoryCell dataKey="productCategories" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Min вес (кг)
          </Table.HeaderCell>
          <MinWeight dataKey="minWeight" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Max вес (кг)
          </Table.HeaderCell>
          <MaxWeight dataKey="maxWeight" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} fullText sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Для калькулятора
          </Table.HeaderCell>

          <ToggleCell
            dataKey="forCalculator"
            disabled={(rowData) => rowData && rowData.forCalculator}
            onChange={onChangeForCalculator}
          />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100} sortable>
          <Table.HeaderCell
            renderSortIcon={(sortType) => (
              <i
                className={classNames(
                  'bi',
                  sortType === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            )}
          >
            Включен
          </Table.HeaderCell>
          <ToggleCell
            dataKey="disabled"
            inverted={true}
            onChange={checkDeliveryMethod}
          />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>
    </>
  );
};
